export const translations = {
  en: {
    homePlansSummary: {
      "title": "Present your work, start with Free",
      "intro": "Create your profile in the Atlas. When you want to expand your content and offer activities, you can upgrade to Pro.",
      "freeTagline": "Start telling the story of your farm, organization or place.",
      "freeDescription": "An Atlas profile, contact details and one product or production entry.",
      "proDescription": "Expand your profile and offer opportunities and projects.",
      "renewal": "Annual subscription with automatic renewal, which you can turn off.",
      "compare": "Compare plan features and terms",
      "freeFaq": "Your personal account is free. With Free, you can present your farm, organization or place in the Atlas, with contact details and one product or production entry.",
      "faqPlans": "Explore Free and Pro."
    },
    profilePathCommon: {
      "atlas": "Explore profiles in the Atlas",
      "accountNote": "Your personal account and activity profile are separate: use your account to create and manage your profiles.",
      "activityNote": "These are examples: check the requirements for each activity. A profile does not automatically enable sales, bookings or payment collection.",
      "formulas": "Discover ways to participate",
      "planFeatures": "Plan features and terms",
      "plansTitle": "Start with Free, expand with Pro",
      "plansSummary": "Free lets you present your farm, organization or place. Pro adds features to expand your profile and offer opportunities.",
      "comparePlans": "Compare Free and Pro",
      "fees": "Fees and receiving payments",
      "representation": "Only present activities, organizations or places you are authorized to represent. Publication does not replace required permissions or checks.",
      "startTitle": "How to get started",
      "faq": "Frequently asked questions",
      "projects": "Browse projects",
      "manageProfiles": "Manage your profiles from the dashboard"
    },
    farmersPage: {
      "title": "For farmers, livestock keepers and nurseries",
      "intro": "Tell the story of your work, your area and the living heritage you care for. Present crops, livestock and production, choosing the information relevant to your activity.",
      "present": "Present your activity",
      "cardsTitle": "Build a profile that represents your work",
      "cards": [
        {
          "title": "Identity and territory",
          "text": "Present your activity, show where you operate and add useful contact details."
        },
        {
          "title": "Crops, species and cultivars",
          "text": "Describe the crops and cultivars you grow, your plots and relevant practices."
        },
        {
          "title": "Animals and livestock",
          "text": "Present your livestock species, breeds and characteristics where relevant."
        },
        {
          "title": "Story, production and contacts",
          "text": "Tell your story with text and images, and present your production and ways to contact you."
        }
      ],
      "optionalNote": "Only complete the sections relevant to your activity.",
      "opportunitiesTitle": "Offer practical ways to participate",
      "opportunities": "With Pro you can offer adoptions, visits, workshops and projects: for example, an adoption with benefits, a farm visit or a hedgerow and pollinator initiative.",
      "detailsTitle": "Details for completing your profile",
      "details": [
        {
          "title": "Crops, cultivars and plots",
          "paragraphs": [
            "Search cultivars by name and available synonyms. If a name is missing from the catalog, you can enter it as a farm-declared name.",
            "Grape and olive have an essential selection, with an extended archive available. Searching by name or synonym searches the full archive.",
            "Selected crops populate their associated areas. Describe plots and practices, including differences from practices inherited from the activity."
          ]
        },
        {
          "title": "Livestock and optional information",
          "paragraphs": [
            "Add one or more livestock units with species, relevant breeds, purposes and husbandry system. You can indicate an unknown breed, crossbreed or declared name; animal count is optional.",
            "Certifications and opportunities are additional information and do not affect the profile completeness percentage. Completeness is not certification.",
            "The profile remains linked to your personal account. An activity’s name does not grant rights to existing profiles: ownership must be verified before management can be assigned; there is no public profile-claiming procedure."
          ]
        }
      ]
    },
    entitiesPage: {
      "title": "For institutions, communities and professionals",
      "intro": "Present your organization, a place you manage or the expertise you offer. Describe activities and projects, and choose how to involve people.",
      "present": "Present your organization or place",
      "cardsTitle": "Choose the right profile",
      "cards": [
        {
          "title": "Municipalities and public bodies",
          "text": "Present your organization, managed spaces and local initiatives, from public green spaces to educational trails."
        },
        {
          "title": "Parks and reserves",
          "text": "Describe the place, its habitats, conservation activities and those responsible for its management."
        },
        {
          "title": "Gardens, oases and educational centers",
          "text": "Present spaces, living heritage and educational activities, including places without protected-area status."
        },
        {
          "title": "Associations, foundations and networks",
          "text": "Describe your organization’s mission, operating area and activities, including any managed places and collaborations."
        },
        {
          "title": "Professionals and practices",
          "text": "Present your expertise, services and operating area. Use the portfolio to describe past work."
        }
      ],
      "organizationTitle": "Organization and place",
      "organization": "An organization’s profile describes who operates; a place’s profile describes the managed space. Where they are distinct, explain the relationship and who is responsible.",
      "opportunitiesTitle": "What you can offer",
      "opportunities": "Different types of promoters can propose projects, visits and other initiatives, according to their activities and the applicable requirements.",
      "examples": [
        {
          "title": "Projects",
          "text": "Restoring a garden or improving a habitat."
        },
        {
          "title": "Learning and visits",
          "text": "Workshops, courses and guided visits."
        },
        {
          "title": "Volunteering",
          "text": "Agreed care and monitoring activities."
        },
        {
          "title": "Adoption and support",
          "text": "Ways to support a place or an initiative."
        }
      ],
      "detailsTitle": "Details for organizations and places",
      "details": [
        {
          "title": "Choosing a category and completing your profile",
          "paragraphs": [
            "For municipalities and public bodies, identify the responsible office, managed spaces and initiatives. Select Food garden management only if you manage a food garden; Agricultural crop production is a separate choice. For public green spaces, select the relevant managed plant heritage scopes.",
            "For a park or reserve, choose Park, garden or educational center, then Park / protected area. State its type, manager, management basis, habitats and conservation activities. Registration does not grant protected status.",
            "For a place without protected-area status, choose Park, garden or educational center, then Garden / educational oasis / environmental center. Specify historic garden, botanical garden, arboretum or environmental education center, and describe its manager, assets, spaces and activities.",
            "For an association or foundation, give the legal name, mission, operating scope and territories. Professionals and practices can present their discipline, services and portfolio; explain how to request availability and a quotation for individual consultancy and site visits.",
            "For trees, distinguish planned, surveyed, cared-for and planted counts, stating the period, source and your role."
          ]
        },
        {
          "title": "Organizations, places, networks and responsibilities",
          "paragraphs": [
            "Organization and place profiles can be separate: explain their relationship and name the manager. Choose one responsible promoter for each project and identify others as partners, avoiding duplicate listings of the same initiative.",
            "Local, regional, national or international scope describes operations, not a verified legal hierarchy. Affiliations and roles are declarations and do not automatically grant access to other profiles.",
            "Membership or volunteering alone does not authorize representation: an appropriate mandate or authority is required. For an informal community, identify who takes responsibility without presenting it as an already recognized legal body.",
            "Ownership must be verified before management of an existing profile can be assigned. Public profile claiming, invitations and shared management are not available."
          ]
        },
        {
          "title": "Projects on public, private or mixed land",
          "paragraphs": [
            "State the objective, place, activities, schedule, progress, partners and participation arrangements. Describe land ownership, available agreements and permissions and, where money is involved, the recipient and use of funds.",
            "Distinguish proposals awaiting approval from authorized work. For public land, involve the competent body; for private land, explain the appropriate title or consent; for mixed land, address all parties.",
            "A profile does not replace agreements, public procedures or required environmental, landscape and archaeological permissions."
          ]
        },
        {
          "title": "Volunteering and activity requirements",
          "paragraphs": [
            "For volunteering, explain contacts, activities, joining procedures, safety and required insurance. This is not unregulated unpaid work.",
            "Visits, courses and professional services are subject to the requirements of each activity. The platform does not award professional qualifications."
          ]
        }
      ]
    },
    howPage: {
      "title": "How PlantAdopting works",
      "subtitle": "Discover a place. Choose how to get involved.",
      "intro": "Explore farms, places and projects. Browse without registering; payments through PlantAdopting require a free account.",
      "atlas": "Explore the Atlas",
      "opportunities": "Discover opportunities",
      "stepsTitle": "Start wherever you like",
      "steps": [
        {
          "title": "1. Find a place that interests you",
          "text": "Search the Atlas and map by location, activity or category for farms, professionals, associations and places.",
          "link": "Explore the map"
        },
        {
          "title": "2. Meet the people behind it",
          "text": "Read the profile’s story, activities and information; use its contacts to learn more.",
          "link": "Open the Atlas"
        },
        {
          "title": "3. Discover what you can do",
          "text": "Find adoptions, projects, visits, learning and other initiatives. Each profile explains how to participate.",
          "link": "Explore opportunities"
        },
        {
          "title": "4. Check the details and choose",
          "text": "Read the amount, duration, benefits and terms. Arrange external activities directly through the promoter’s website or contacts."
        }
      ],
      "formulasTitle": "Four ways to adopt and support",
      "formulas": [
        {
          "title": "Adoption with benefits",
          "text": "Support a farm, organization or place and receive the offered products, services or experiences. Check duration, benefits and delivery or participation arrangements."
        },
        {
          "title": "Symbolic support",
          "text": "Support the care of plants, animals or places without buying them. Any updates or recognition are described in the offer."
        },
        {
          "title": "Project contribution",
          "text": "Help a practical initiative. Read its declared objective, recipient and use of funds; ask if information is missing."
        },
        {
          "title": "Sponsorship",
          "text": "Support an initiative with visibility or recognition agreed with the promoter and described in the offer."
        }
      ],
      "noPro": "You do not need Pro to participate. Amounts, benefits and terms are shown in each offer.",
      "promoterLink": "Proposing an initiative? Read about plans, fees and receiving payments.",
      "otherParagraphs": [
        "Find products, visits, courses and volunteering through promoters’ websites or contacts.",
        "Check availability, terms and booking requirements: finding an activity in the Atlas is not a booking."
      ],
      "questions": [
        {
          "question": "Do I need to register?",
          "answer": "Not to explore. Paying through PlantAdopting requires a free account, without Pro. For enquiries and external activities, follow the channel provided."
        },
        {
          "question": "How can I check a payment?",
          "answer": "Check “Your payments”: returning to the website after checkout does not confirm successful payment. Live charges are real."
        },
        {
          "question": "What if a project does not reach its target?",
          "answer": "Missing a funding target does not automatically trigger a refund: there is no all-or-nothing mechanism. Read the offer’s terms; your rights and any more favourable conditions still apply."
        }
      ],
      "paymentHistory": "Your payments",
      "projects": "Browse projects",
      "transparencyTitle": "Before you take part",
      "transparency": "Promoters publish the information. An Atlas listing does not independently verify statements or permissions. Read the profile and terms; if in doubt, contact the promoter.",
      "permissions": "A proposal, permission to act and activity availability are separate.",
      "terms": "Terms of use",
      "paymentTerms": "Participation and payment terms",
      "platformSupport": "Supporting PlantAdopting contributes to the platform’s development. It is separate from supporting individual farms, organizations, places and their projects.",
      "finalTitle": "Choose how to participate",
      "exploreOpportunities": "Explore opportunities",
      "register": "Create a free account",
      "faq": "More questions? Read the FAQs",
      "detailsTitle": "Want to know more?",
      "optionsDetailsTitle": "Visits, courses, products and volunteering",
      "accountDetailsTitle": "Accounts and payment confirmation",
      "goalsDetailsTitle": "Project targets and refunds",
      "promotersDetailsTitle": "About promoters and proposals",
      "projectGoal": "The funding target is the amount the promoter aims to raise for the project.",
      "transparencyShort": "Information is provided by promoters. Before taking part, read the offer and its terms."
    },
    plansPage: {
      "title": "Free and Pro: choose how to participate",
      "intro": "You can explore PlantAdopting without registering. To present your farm, organization or place, start with Free. Pro lets you add more products and create opportunities and projects.",
      "personalAccount": "Only interested in taking part in initiatives? Your personal account is free and does not require Pro.",
      "offersNote": "Individual offers may have their own costs and terms.",
      "cardsTitle": "Choose a plan",
      "freeTagline": "Start telling the story of your farm, organization or place.",
      "freePoints": [
        "An Atlas profile and contact details.",
        "Information about your activities and the living heritage you care for.",
        "One product or production entry and one brand."
      ],
      "proTagline": "Add more products and offer activities.",
      "proPoints": [
        "Up to {products} products or production entries.",
        "Up to {opportunities} opportunities in total, including projects.",
        "Adoptions, visits and other activities supported by the platform."
      ],
      "optionalData": "Add products, crops and heritage information when relevant to your profile.",
      "annualPrice": "{price}/year, including VAT",
      "renewalShort": "One annual payment. Renews automatically each year; you can turn renewal off in the subscription portal.",
      "publicException": "Municipalities and public bodies can publish one project with Free.",
      "comparisonTitle": "What each plan includes",
      "scrollTable": "On smaller screens, scroll the table horizontally.",
      "activation": "To activate Pro, complete your purchase in “Plan and payments”.",
      "activationNote": "Selecting Pro during registration does not complete activation: payment is required. Review the amount and terms before confirming; live-mode charges are real.",
      "terms": "Subscription and payment terms",
      "refundNote": "The full terms also explain the refund guarantees.",
      "feesLabels": [
        "Commercial adoption with Pro",
        "Sponsorship",
        "Symbolic support",
        "Project contribution"
      ],
      "feesNote": "Platform fees are charged to the promoter. Payment processor costs are separate and still apply when the platform fee is zero.",
      "legacyFee": "Any existing commercial adoptions on Free retain the {fee} fee; this does not enable new opportunities.",
      "contributionPoints": [
        "Pro gives access to features; it is not an adoption and does not automatically fund a project.",
        "Contributions to individual initiatives follow the terms shown in their profiles.",
        "Supporting PlantAdopting helps develop the platform; it does not activate Pro or automatically distribute money to listed farms, organizations or places."
      ],
      "startTitle": "Where would you like to start?",
      "startFree": "Start for free",
      "present": "Present your work",
      "managePlan": "Manage your plan",
      "manageSubscription": "Manage your subscription",
      "chooseProfile": "Choose a profile to manage",
      "dashboard": "Open your dashboard",
      "formulas": "Ways to participate",
      "faq": "FAQs",
      "loading": "Loading plan information…",
      "commercialError": "We could not verify the current price and plan limits. Try again or read the full terms.",
      "profilesError": "We could not verify your profiles. Open your dashboard to choose the plan to manage.",
      "checkingAccount": "Checking your account…",
      "retry": "Try again",
      "tableOpportunityLimit": "{count} total, including projects",
      "tableProjectLimit": "Within the {count} opportunities",
      "detailsTitle": "Pro and payment details",
      "subscriptionDetailsTitle": "Pro subscription: activation, renewal and cancellation",
      "paymentsDetailsTitle": "Receiving payments and fees on offers",
      "contributionsDetailsTitle": "Subscriptions and contributions: the difference",
      "receivingPayments": "To receive payments for your offers, connect and enable the recipient’s Stripe account.",
      "subscriptionDetails": [
        "The subscription applies to each profile, with annual payment and automatic renewal.",
        "You can turn renewal off in the subscription portal. Pro stays active until the end of the period already paid for.",
        "Cancellation does not delete your profile or automatically issue a refund."
      ]
    },
    homePolish: {
      "storyEyebrow": "A story from the Atlas",
      "storyTitle": "From the olive grove to the Atlas",
      "storyDescription": "A day of olive harvesting at Azienda Agricola Bellanca Vittoria, with the people and hands-on work behind it. Every place has a story to tell. Yours does too.",
      "storyProfile": "Explore Bellanca’s profile",
      "watchVideo": "Watch the video",
      "videoLanguage": "Italian audio · English subtitles",
      "videoUnsupported": "Your browser does not support video.",
      "videoUnavailable": "Video unavailable. Try again or",
      "openVideoFile": "open the file",
      "heroDescription": "Discover farms, professionals, places and projects that care for the land. Or present your work in the Atlas.",
      "heroPlanNote": "Start with Free. Upgrade to Pro if you need more features.",
      "exploreWithoutAccount": "Just browsing? You can explore the Atlas without registering.",
      "benefitAdoption": "Support a farm, organization or place through an adoption and receive the benefits specified in the offer, such as products or experiences.",
      "freeSummary": "Free: present your farm, organization or place in the Atlas, with contact details, information about your activities and one product or production.",
      "proSummary": "Pro: expand your profile with more products and opportunities — {annualPrice}."
    },
    content: {"loading": "Loading…", "retry": "Try again", "catalogError": "The crop catalog could not be loaded. This is a loading error, not an empty catalog.", "catalogEmpty": "No crop categories are available yet. Explore all subjects or come back later.", "cropMissing": "Crop category not found", "cropError": "The crop profile could not be loaded.", "cropEmpty": "No subjects currently declare this crop. Explore the full atlas or register your activity.", "homeEmpty": "The atlas is ready for its first profiles. Register an activity or explore projects as they are added.", "homeError": "Profiles could not be loaded right now. Try again or open the atlas.", "noDescription": "The subject has not provided a description yet.", "profileError": "Subject profile unavailable", "profileErrorText": "The profile may be missing or temporarily unavailable. Return to the atlas to find another subject.", "profileMissing": "Subject profile not found", "emptyProjects": "No projects have been added yet. Municipalities and public bodies can create one project with Free; other promoters need Pro.", "noProjectMatch": "No projects match this search. Clear the filters to browse all projects.", "emptySubjects": "No profiles are available in this category yet. Choose another category or register an activity you own."},
    planComparison: {
      proAnnualPrice: "€50/year, including VAT",
      newOffersRequirePro: "New offers require Pro",
      billingNote: "Pro: {annualPrice}. One annual charge; approximately €4.17/month for reference only. New opportunities require Pro. Sponsorships: 5%; symbolic support and contributions: 0%. Payment processor fees are separate.",
    },
    nav: {
      explore: "Explore",
      crops: "Crops",
      howItWorks: "How it works",
      forFarmers: "For farmers and livestock keepers",
      about: "About",
      signIn: "Sign in",
      listFarm: "Register a subject",
      presentYourReality: "Present your work",
      search: "Search",
      menu: "Menu",
    },
    home: {
      kicker: "Grow connections. Protect living heritage.",
      heroLine1: "Adopt nature.",
      heroLine2: "Support the future.",
      heroLine3: "Support how they grow.",
      heroSupport: "A place where farms, communities, institutions and professionals turn living heritage into tangible projects to support and follow over time.",
      searchPlaceholder: "Search subjects, crops, varieties or places…",
      exploreMap: "Explore the map",
      listFarmFree: "Create a Free subject profile",
      statFarms: "Subjects",
      statCrops: "Crop categories",
      statCountries: "Countries",
      exploreByCrop: "Explore by crop",
      exploreByCropSub: "Browse agriculture by what grows in the ground.",
      viewAllCrops: "View all crops",
      moreCrops: "More crops",
      moreCropsSub: "Explore all",
      featuredFarms: "Meet the subjects",
      featuredFarmsSub: "Meet the people and organizations caring for living heritage.",
      viewAllFarms: "View the subject atlas",
      forFarmersTitle: "Care for a place? Share your work.",
      forFarmersText: "Register a farm, livestock holding, community, institution or professional activity. Describe your living heritage and offer clear ways to participate.",
      farmsCount: "subjects",
    },
    explore: {
      title: "Explore the map",
      subtitle: "Discover subjects, territories and opportunities. Filter agricultural activity by crops and varieties.",
      searchPlaceholder: "Search subject, crop, variety or place…",
      searchLabel: "Search subjects, crops, varieties or places",
      filtersLoading: "Loading crop and practice filters…",
      filtersError: "Crop and practice filters could not be loaded. Retry without clearing your search.",
      referenceCatalogLoading: "Loading crop and botanical filters…",
      referenceCatalogError: "Crop and botanical filters are unavailable.",
      animalCatalogLoading: "Loading animal species filters…",
      animalCatalogError: "Animal species filters are unavailable.",
      allCountries: "All countries",
      allCrops: "All crops",
      allRegions: "All regions",
      allVarieties: "All varieties", botanicalSpecies: "Botanical species", botanicalCultivar: "Cultivar",
      allPractices: "All practices",
      practicesFeatures: "Practices & features",
      selectCropFirst: "Select a crop first",
      practiceCats: {
        water_management: "Water management",
        water_source: "Water source",
        soil: "Soil management",
        biodiversity: "Biodiversity",
        cropping_system: "Cropping system",
      },
      moreFilters: "More filters",
      clear: "Clear filters",
      country: "Country",
      region: "Region",
      crop: "Crop",
      variety: "Variety",
      practice: "Farming practice",
      resultsFound: "subjects found",
      viewFarm: "View profile",
      noResults: "No subjects match these filters. Try a broader search.",
      mainCrop: "Main crop",
    },
    farm: {
      botanicalDetails: "Botanical species and cultivars",
      overview: "Overview",
      fields: "Fields",
      cropsVarieties: "Crops & Varieties",
      howWeGrow: "How We Grow",
      products: "Products",
      experiences: "Experiences",
      supportAdopt: "Support & Adopt",
      aboutFarm: "About this subject",
      location: "Location",
      mainCrops: "Main crops",
      contact: "Contact",
      website: "Website",
      profileCompleteness: "Profile completeness",
      ourFields: "Areas and plots",
      area: "Area",
      crop: "Crop",
      trees: "Trees",
      orchardAge: "Planting age",
      plantingSpacing: "Planting spacing",
      scientificName: "Scientific name",
      varieties: "Varieties",
      toBeVerified: "To be verified",
      waterManagement: "Water Management",
      soilManagement: "Soil Management",
      biodiversity: "Biodiversity",
      agriculturalPractices: "Agricultural Practices",
      waterSource: "Water source",
      fromThisFarm: "Products and services",
      visitShop: "Visit farm shop",
      connect: "Connect",
      years: "years",
      yearsOld: "years old",
      viewAllProducts: "View all products",
      noValuesYet: "Information not yet provided.",
      demoFarm: "DEMO profile",
      farmLabel: "Subject",
      farmerDeclared: "Declared by the subject",
      declaredNote: "Profile information is declared by its subject and not independently verified unless stated. Completeness is not certification.",
      lastUpdated: "Last updated",
      contactFarm: "Contact the subject",
      learnMore: "Learn more",
    },
    onboarding: {
      title: "Register a subject",
      subtitle: "Create a subject profile in a few minutes. It's free.",
      step: "Step",
      of: "of",
      step1: "Create your subject profile",
      step2: "What do you grow?",
      step3: "Tell your story",
      step4: "Add contact information",
      farmName: "Public subject name",
      country: "Country",
      region: "Region",
      locality: "Location",
      selectCrops: "Select one or more crops", botanicalSpecies: "Botanical species (optional)", selectSpecies: "Select a species", botanicalCultivar: "Cultivar (optional)", selectCultivar: "Select a cultivar", noCultivar: "Not specified", otherCultivar: "Other cultivar", otherCultivarPlaceholder: "Enter cultivar name", otherCultivarNote: "User-provided names are not automatically verified or added to the catalog.",
      description: "Short description",
      mainImage: "Cover image",
      websiteOpt: "Website (optional)",
      shopOpt: "Shop URL (optional)",
      next: "Next",
      back: "Back",
      finish: "Create the subject profile",
      doneTitle: "Your subject profile has been created.",
      doneSub: "Complete its information and participation terms from the dashboard. Profile completeness is not verification.",
      profileComplete: "Your profile is",
      complete: "complete",
      enrich: "Add more to your profile",
      viewMyFarm: "View the subject profile", goDashboard: "Go to my dashboard",
      required: "This field is required",
      addVarieties: "Add crop varieties",
      addField: "Add your first area / plot",
      tellHow: "Tell us how you grow",
      addCerts: "Add certifications",
      addProducts: "Add a product / production entry",
      addOpps: "Activate Pro to add opportunities",
    },
    completeness: {
      subject_identity: "Subject identity", description: "Description", contact: "Contact information",
      farm_identity: "Subject identity",
      location: "Location",
      main_crop: "Crops specified",
      field_information: "Field information",
      varieties: "Add varieties",
      farming_practices: "Add farming practices",
      certifications: "Add certifications",
      opportunities: "Add opportunities",
    },
    opp: {
      BUY: "Buy", ADOPT: "Adopt", VISIT: "Visit", LEARN: "Learn",
      VOLUNTEER: "Volunteer", SUPPORT: "Support", PROJECT: "Project",
    },
    footer: {
      tagline: "Adopt nature. Support the future.",
      explore: "Explore",
      company: "PlantAdopting",
      about: "About",
      forFarmers: "For farmers and livestock keepers",
      howItWorks: "How it works",
      crops: "Crops",
      support: "Support PlantAdopting",
      note: "PlantAdopting connects living heritage, subjects and territories. Profile information is declared by its subjects. Enabled payments are processed through Stripe. Live charges are real.",
      rights: "All rights reserved.",
    },
    support: {
      title: "Support PlantAdopting",
      intro: "PlantAdopting is an atlas of living heritage connecting people, farms, institutions, professionals and places. Support contributes to platform development.",
      howHeading: "How support could help",
      howSub: "As the project grows, support could help PlantAdopting:",
      disclaimer: "PlantAdopting is a project in development and not a nonprofit, charity, foundation or association. Voluntary platform support is separate from subject payments and subscriptions. Check availability, amount and terms at checkout; enabled live payments are real. Tax deductibility is not promised.",
    },
    auth: {
      login: "Log in", register: "Create account", logout: "Log out",
      email: "Email", password: "Password", name: "Name",
      signInTitle: "Welcome back", signInSub: "Log in to participate, view your payments and manage your subjects.",
      registerTitle: "Create your account", registerSub: "One free personal account to participate and manage your activities.",
      noAccount: "Don't have an account?", haveAccount: "Already have an account?",
      myDashboard: "My Dashboard", myFarm: "My subject", editFarm: "Edit subject profile",
      dashboardTitle: "My Dashboard", yourFarms: "Your subjects", allFarms: "All subjects", farmFilter_all: "All", farmFilter_assigned: "Assigned", farmFilter_unassigned: "Unassigned", noMatchingFarms: "No subjects match this filter.", farmsLoadError: "Could not load subjects. Please try again later.", ownerAssignedStatus: "Owner assigned", ownerUnassignedStatus: "No owner assigned", ownerAssign: "Assign owner", ownerChange: "Change owner", ownerEmailLabel: "Registered owner email", ownerSave: "Save owner", ownerCancel: "Cancel", ownerInvalidEmail: "Enter a valid email address.", ownerNotFound: "No registered account was found for this email.", ownerAssignError: "Could not assign owner. Please try again.", ownerAssigned: "Subject owner updated successfully.",
      noFarms: "No subjects are linked to your account yet. You can participate as an adopter or register an activity you own. Existing profiles require verified assignment.",
      openPublic: "View public profile", edit: "Edit", plan: "Plan",
      save: "Save changes", saving: "Saving…", saved: "Changes saved.",
      saveError: "Could not save. Please try again.", loading: "Loading…",
      required: "Required", passwordHint: "At least 8 characters",
      adminBadge: "Admin", ownerBadge: "Owner",
      secGeneral: "General information", secFields: "Areas / plots", secCrops: "Crops & varieties",
      secPractices: "Farming practices", secBrands: "Brands & products", secOpps: "Opportunities",
      farmName: "Public subject name", description: "Description (English)", descriptionIt: "Description (Italian)",
      locality: "Locality", municipality: "Municipality", region: "Region", country: "Country",
      mainImage: "Cover image", website: "Website", contactEmail: "Contact email", shopUrl: "Shop URL",
      addField: "Add area / plot", addCrop: "Add crop", addProduct: "Add product", addOpportunity: "Add opportunity",
      remove: "Remove", fieldNameLabel: "Field name", areaHa: "Area (ha)", crop: "Crop", trees: "Trees",
      waterMethods: "Water methods (comma-separated)", waterSource: "Water source",
      scientificName: "Scientific name", varieties: "Varieties (comma-separated)",
      brandName: "Brand name", productName: "Product name", externalUrl: "External link",
      soil: "Soil management (comma-separated)", biodiversity: "Biodiversity (comma-separated)",
      oppType: "Type", oppTitle: "Title", backToDash: "Back to dashboard",
    },
    pages: {
      howTitle: "How PlantAdopting works",
      howSub: "An atlas of living heritage connecting people, subjects, territories and projects.",
      forFarmersTitle: "Put your activity on the map",
      forFarmersSub: "Join a growing atlas of real farms. Share your story and connect directly with people who care about what you grow.",
      aboutTitle: "About PlantAdopting",
      aboutSub: "An atlas of living heritage, people and territories.",
      cropsTitle: "Crops, species and cultivars",
      cropsSub: "Explore agricultural categories and profiles that declare these crops.",
    },
  },
  it: {
    homePlansSummary: {
      "title": "Presenta la tua realtà, inizia con Free",
      "intro": "Crea il tuo profilo nell’Atlante. Quando vuoi ampliare i contenuti e proporre iniziative, puoi passare a Pro.",
      "freeTagline": "Per iniziare a raccontare la tua realtà.",
      "freeDescription": "Profilo nell’Atlante, contatti e un prodotto o una produzione.",
      "proDescription": "Per ampliare il profilo e proporre opportunità e progetti.",
      "renewal": "Abbonamento annuale con rinnovo automatico, disattivabile.",
      "compare": "Confronta funzionalità e condizioni dei piani",
      "freeFaq": "L’account personale è gratuito. Con Free puoi presentare la tua realtà nell’Atlante, con contatti e un prodotto o una produzione.",
      "faqPlans": "Scopri Free e Pro."
    },
    profilePathCommon: {
      "atlas": "Esplora le realtà nell’Atlante",
      "accountNote": "L’account personale e il profilo dell’attività sono distinti: dall’account puoi creare e gestire le tue schede.",
      "activityNote": "Gli esempi sono indicativi: verifica i requisiti dell’attività. La scheda non attiva automaticamente vendite, prenotazioni o incassi.",
      "formulas": "Scopri le formule di partecipazione",
      "planFeatures": "Funzionalità e condizioni del piano",
      "plansTitle": "Inizia con Free, amplia con Pro",
      "plansSummary": "Con Free puoi presentare la tua realtà. Pro aggiunge funzionalità per ampliare i contenuti e proporre opportunità.",
      "comparePlans": "Confronta Free e Pro",
      "fees": "Commissioni e modalità di incasso",
      "representation": "Presenta soltanto attività, organizzazioni o luoghi che sei autorizzato a rappresentare. La pubblicazione non sostituisce autorizzazioni o verifiche richieste.",
      "startTitle": "Come iniziare",
      "faq": "Domande frequenti",
      "projects": "Consulta i progetti",
      "manageProfiles": "Gestisci le tue schede dalla dashboard"
    },
    farmersPage: {
      "title": "Per agricoltori, allevatori e vivai",
      "intro": "Racconta la tua attività, il territorio e il patrimonio vivente che curi. Presenta colture, allevamenti e produzioni, scegliendo le informazioni pertinenti alla tua realtà.",
      "present": "Presenta la tua attività",
      "cardsTitle": "Costruisci una scheda che ti rappresenta",
      "cards": [
        {
          "title": "Identità e territorio",
          "text": "Presenta la tua attività, indica dove operi e aggiungi i contatti utili."
        },
        {
          "title": "Colture, specie e varietà",
          "text": "Descrivi le colture e le varietà che coltivi, gli appezzamenti e le pratiche pertinenti."
        },
        {
          "title": "Animali e allevamento",
          "text": "Presenta specie, razze e caratteristiche del tuo allevamento, quando pertinenti."
        },
        {
          "title": "Storia, produzioni e contatti",
          "text": "Racconta la tua storia con testi e immagini e presenta le produzioni e i canali per contattarti."
        }
      ],
      "optionalNote": "Compila soltanto le sezioni pertinenti alla tua attività.",
      "opportunitiesTitle": "Offri modi concreti per partecipare",
      "opportunities": "Con Pro puoi proporre adozioni, visite, laboratori e progetti: per esempio, un’adozione con benefici, una visita aziendale o un’iniziativa per siepi e impollinatori.",
      "detailsTitle": "Dettagli per compilare il profilo",
      "details": [
        {
          "title": "Colture, varietà e appezzamenti",
          "paragraphs": [
            "Cerca le varietà per nome e sinonimi disponibili. Se una denominazione non è nel catalogo, puoi indicarla come dichiarata dall’azienda.",
            "Per vite e olivo trovi una selezione essenziale; puoi consultare l’archivio esteso. La ricerca per nome o sinonimo consulta tutto l’archivio.",
            "Le colture inserite alimentano le relative aree. Puoi specificare appezzamenti e pratiche e indicare le differenze rispetto alle pratiche ereditate dall’attività."
          ]
        },
        {
          "title": "Allevamento e informazioni facoltative",
          "paragraphs": [
            "Aggiungi una o più unità di allevamento con specie, razze pertinenti, finalità e sistema di allevamento. Puoi indicare razza sconosciuta, incrocio o una denominazione dichiarata; il numero di capi è facoltativo.",
            "Certificazioni e opportunità sono informazioni aggiuntive e non incidono sulla percentuale di completezza della scheda. La completezza non equivale a una certificazione.",
            "Il profilo resta collegato al tuo account personale. Il nome dell’attività non attribuisce diritti su schede già esistenti: per acquisirne la gestione occorre verificare la titolarità; non è disponibile una procedura pubblica di rivendicazione."
          ]
        }
      ]
    },
    entitiesPage: {
      "title": "Per enti, comunità e professionisti",
      "intro": "Presenta la tua organizzazione, il luogo che gestisci o le competenze che offri. Racconta attività e progetti e scegli come coinvolgere le persone.",
      "present": "Presenta la tua realtà",
      "cardsTitle": "Scegli la scheda adatta",
      "cards": [
        {
          "title": "Comuni ed enti pubblici",
          "text": "Presenta l’ente, gli spazi gestiti e le iniziative sul territorio: dal verde pubblico ai percorsi educativi."
        },
        {
          "title": "Parchi e riserve",
          "text": "Racconta il luogo, gli habitat, le attività di conservazione e chi ne cura la gestione."
        },
        {
          "title": "Giardini, oasi e centri educativi",
          "text": "Presenta spazi, patrimonio e attività educative, anche per luoghi senza status di area protetta."
        },
        {
          "title": "Associazioni, fondazioni e reti",
          "text": "Racconta missione, ambito operativo e attività della tua organizzazione, indicando eventuali luoghi gestiti e collaborazioni."
        },
        {
          "title": "Professionisti e studi",
          "text": "Presenta competenze, servizi e territorio operativo. Usa il portfolio per raccontare i lavori svolti."
        }
      ],
      "organizationTitle": "Organizzazione e luogo",
      "organization": "L’organizzazione racconta chi opera; la scheda del luogo racconta lo spazio gestito. Quando sono distinti, chiarisci il rapporto e chi ne è responsabile.",
      "opportunitiesTitle": "Cosa puoi proporre",
      "opportunities": "Progetti, visite e altre iniziative possono essere proposti da soggetti diversi, secondo le attività svolte e i requisiti applicabili.",
      "examples": [
        {
          "title": "Progetti",
          "text": "Recupero di un giardino o miglioramento di un habitat."
        },
        {
          "title": "Formazione e visite",
          "text": "Laboratori, corsi e visite guidate."
        },
        {
          "title": "Volontariato",
          "text": "Attività concordate di cura e monitoraggio."
        },
        {
          "title": "Adozione e sostegno",
          "text": "Formule per sostenere un luogo o un’iniziativa."
        }
      ],
      "detailsTitle": "Dettagli per organizzazioni e luoghi",
      "details": [
        {
          "title": "Scegliere la categoria e compilare il profilo",
          "paragraphs": [
            "Per Comuni ed enti pubblici indica ufficio responsabile, spazi gestiti e interventi. Seleziona Gestione di orti solo se gestisci un orto; Coltivazioni agricole è una scelta distinta. Per il verde pubblico usa gli ambiti pertinenti del patrimonio vegetale.",
            "Per un parco o una riserva scegli Parco, giardino o centro educativo, poi Parco / area protetta. Indica tipologia, gestore, titolo di gestione, habitat e conservazione. La registrazione non attribuisce uno status di protezione.",
            "Per un luogo senza status di area protetta scegli Parco, giardino o centro educativo, poi Giardino / oasi educativa / centro ambientale. Specifica se è un giardino storico, orto botanico, arboreto o centro di educazione ambientale e descrivi gestore, patrimonio, spazi e attività.",
            "Per un’associazione o fondazione indica denominazione legale, missione, ambito operativo e territori. Per professionisti e studi presenta disciplina, servizi e portfolio; per consulenze e sopralluoghi individuali indica come chiedere disponibilità e preventivo.",
            "Per le alberature distingui conteggi previsti, censiti, curati e messi a dimora, indicando periodo, fonte e ruolo."
          ]
        },
        {
          "title": "Organizzazione, luogo, reti e responsabilità",
          "paragraphs": [
            "La scheda dell’organizzazione e quella del luogo possono essere distinte: indica il rapporto e il gestore. Per ogni progetto scegli un promotore responsabile e cita gli altri soggetti come partner, evitando duplicati della stessa iniziativa.",
            "L’ambito locale, regionale, nazionale o internazionale descrive l’operatività, non prova una gerarchia legale. Affiliazioni e ruoli sono dichiarati e non assegnano automaticamente accessi ad altre schede.",
            "Essere socio o volontario non basta a rappresentare un’organizzazione: occorre una delega o altro titolo appropriato. Per una comunità informale indica chi si assume la responsabilità, senza presentarla come ente già riconosciuto.",
            "Per acquisire la gestione di una scheda esistente occorre verificare la titolarità. Non sono disponibili una procedura pubblica di rivendicazione, inviti o gestione condivisa."
          ]
        },
        {
          "title": "Progetti su aree pubbliche, private o miste",
          "paragraphs": [
            "Indica obiettivo, luogo, attività, periodo, avanzamento, partner e modalità di partecipazione. Specifica proprietà dell’area, disponibilità di accordi e autorizzazioni e, se sono previste risorse economiche, beneficiario e impiego dei fondi.",
            "Distingui una proposta in attesa di assensi da un intervento già autorizzato. Su aree pubbliche coinvolgi l’ente competente; su aree private chiarisci il titolo o consenso; su aree miste considera tutte le parti.",
            "La scheda non sostituisce convenzioni, procedure pubbliche o autorizzazioni ambientali, paesaggistiche e archeologiche richieste."
          ]
        },
        {
          "title": "Volontariato e requisiti delle attività",
          "paragraphs": [
            "Per il volontariato chiarisci referente, attività, modalità di adesione, sicurezza e coperture richieste. Non è una forma di lavoro gratuito senza regole.",
            "Visite, corsi e servizi professionali richiedono i requisiti pertinenti all’attività. La piattaforma non attribuisce titoli o qualifiche professionali."
          ]
        }
      ]
    },
    howPage: {
      "title": "Come funziona PlantAdopting",
      "subtitle": "Scopri una realtà. Scegli come partecipare.",
      "intro": "Scopri aziende, luoghi e progetti e chi li cura. Esplora senza registrarti; per pagare tramite PlantAdopting serve un account gratuito.",
      "atlas": "Esplora l’Atlante",
      "opportunities": "Scopri le opportunità",
      "stepsTitle": "Puoi iniziare da qui",
      "steps": [
        {
          "title": "1. Trova una realtà che ti interessa",
          "text": "Esplora Atlante e mappa per territorio, attività o categoria. Scopri aziende, professionisti, associazioni e luoghi.",
          "link": "Esplora la mappa"
        },
        {
          "title": "2. Conosci chi c’è dietro",
          "text": "Apri il profilo e leggi storia, attività e informazioni disponibili. Per saperne di più, usa i contatti indicati.",
          "link": "Apri l’Atlante"
        },
        {
          "title": "3. Scopri cosa puoi fare",
          "text": "Cerca adozioni, progetti, visite, formazione e altre iniziative. Ogni scheda descrive la proposta e come partecipare.",
          "link": "Esplora le opportunità"
        },
        {
          "title": "4. Controlla i dettagli e scegli",
          "text": "Leggi importo, durata, benefici e condizioni. Per attività su siti esterni o tramite contatti, accordati direttamente con il promotore sul canale indicato."
        }
      ],
      "formulasTitle": "Quattro formule di adozione e sostegno",
      "formulas": [
        {
          "title": "Adozione con benefici",
          "text": "Sostieni una realtà e ricevi prodotti, servizi o esperienze previsti dall’offerta. Leggi durata, benefici e modalità di consegna o partecipazione."
        },
        {
          "title": "Sostegno simbolico",
          "text": "Sostieni la cura di piante, animali o luoghi, senza acquistarli. Aggiornamenti o riconoscimenti sono descritti nell’offerta."
        },
        {
          "title": "Contributo a progetto",
          "text": "Aiuta un’iniziativa concreta. Consulta obiettivo, beneficiario e impiego dichiarato dei fondi; chiedi chiarimenti se mancano informazioni."
        },
        {
          "title": "Sponsorizzazione",
          "text": "Sostieni un’iniziativa con visibilità o riconoscimenti concordati con il promotore e descritti nell’offerta."
        }
      ],
      "noPro": "Per partecipare non serve Pro. Importi, benefici e condizioni sono indicati nelle singole offerte.",
      "promoterLink": "Proponi un’iniziativa? Consulta piani, commissioni e modalità di incasso.",
      "otherParagraphs": [
        "Trovi prodotti, visite, corsi e volontariato, anche tramite siti e contatti dei promotori.",
        "Verifica disponibilità, condizioni e prenotazioni prima di organizzarti: trovare un’attività nell’Atlante non significa averla prenotata."
      ],
      "questions": [
        {
          "question": "Devo registrarmi?",
          "answer": "Per esplorare no; per pagare tramite PlantAdopting serve un account gratuito, senza Pro. Per contatti e attività esterne segui il canale indicato."
        },
        {
          "question": "Come verifico un pagamento?",
          "answer": "Controlla l’esito in «I tuoi pagamenti»: tornare al sito dopo il checkout non conferma che il pagamento sia riuscito. I pagamenti live sono reali."
        },
        {
          "question": "Che cosa succede se un progetto non raggiunge l’obiettivo?",
          "answer": "Se l’obiettivo economico non viene raggiunto, non scatta un rimborso automatico: non c’è un meccanismo «tutto o niente». Consulta le condizioni del progetto; restano validi i tuoi diritti e le condizioni più favorevoli dell’offerta."
        }
      ],
      "paymentHistory": "I tuoi pagamenti",
      "projects": "Consulta i progetti",
      "transparencyTitle": "Prima di partecipare",
      "transparency": "Le informazioni sono pubblicate dai rispettivi promotori. La presenza nell’Atlante non costituisce, da sola, una verifica indipendente delle dichiarazioni o delle autorizzazioni. Leggi la scheda e le condizioni e, se hai dubbi, contatta chi propone l’iniziativa.",
      "permissions": "Proposta, autorizzazione e disponibilità dell’attività sono distinte.",
      "terms": "Termini di utilizzo",
      "paymentTerms": "Condizioni di partecipazione e pagamenti",
      "platformSupport": "Sostenere PlantAdopting significa contribuire allo sviluppo della piattaforma. È distinto dal sostegno alle singole realtà e ai loro progetti.",
      "finalTitle": "Scegli come partecipare",
      "exploreOpportunities": "Esplora le opportunità",
      "register": "Crea un account gratuito",
      "faq": "Hai altre domande? Leggi le FAQ",
      "detailsTitle": "Vuoi saperne di più?",
      "optionsDetailsTitle": "Visite, corsi, prodotti e volontariato",
      "accountDetailsTitle": "Account e conferma dei pagamenti",
      "goalsDetailsTitle": "Obiettivi dei progetti e rimborsi",
      "promotersDetailsTitle": "Informazioni sui promotori e sulle proposte",
      "projectGoal": "L’obiettivo economico indica l’importo che il promotore punta a raccogliere per il progetto.",
      "transparencyShort": "Le informazioni sono dichiarate dai promotori. Prima di partecipare, leggi la scheda e le condizioni dell’offerta."
    },
    plansPage: {
      "title": "Free e Pro: scegli come partecipare",
      "intro": "Puoi esplorare PlantAdopting senza registrarti. Se vuoi presentare la tua realtà, inizia con Free. Con Pro puoi aggiungere più prodotti e creare opportunità e progetti.",
      "personalAccount": "Vuoi soltanto partecipare alle iniziative? L’account personale è gratuito e non richiede Pro.",
      "offersNote": "Le singole offerte possono prevedere costi e condizioni propri.",
      "cardsTitle": "Scegli il piano",
      "freeTagline": "Per iniziare a raccontare la tua realtà.",
      "freePoints": [
        "Profilo nell’Atlante e contatti.",
        "Informazioni sulle attività e sul patrimonio che curi.",
        "Un prodotto o produzione e un marchio."
      ],
      "proTagline": "Per aggiungere prodotti e proporre iniziative.",
      "proPoints": [
        "Fino a {products} prodotti o produzioni.",
        "Fino a {opportunities} opportunità complessive, progetti inclusi.",
        "Adozioni, visite e altre attività previste dalla piattaforma."
      ],
      "optionalData": "Prodotti, colture e dati sul patrimonio si aggiungono quando pertinenti alla tua realtà.",
      "annualPrice": "{price}/anno, IVA inclusa",
      "renewalShort": "Pagamento unico annuale. Rinnovo automatico annuale, disattivabile dal portale abbonamenti.",
      "publicException": "Comuni ed enti pubblici possono pubblicare un progetto anche con Free.",
      "comparisonTitle": "Cosa comprende ogni piano",
      "scrollTable": "Su schermi piccoli puoi scorrere la tabella orizzontalmente.",
      "activation": "Per attivare Pro, completa l’acquisto dalla sezione «Piano e incassi».",
      "activationNote": "Scegliere Pro durante la registrazione non completa l’attivazione: serve il pagamento. Prima di confermare, verifica importo e condizioni; gli addebiti in modalità live sono reali.",
      "terms": "Abbonamenti e pagamenti",
      "refundNote": "Le condizioni complete descrivono anche le garanzie di rimborso.",
      "feesLabels": [
        "Adozione commerciale con Pro",
        "Sponsorizzazione",
        "Sostegno simbolico",
        "Contributo a progetto"
      ],
      "feesNote": "Le commissioni della piattaforma sono a carico del promotore. I costi del servizio di pagamento sono separati e restano applicabili anche quando la commissione della piattaforma è pari a zero.",
      "legacyFee": "Per eventuali adozioni commerciali preesistenti su Free resta la tariffa del {fee}: non abilita nuove opportunità.",
      "contributionPoints": [
        "Pro dà accesso alle funzionalità: non è un’adozione e non finanzia automaticamente un progetto.",
        "I contributi alle singole iniziative seguono le condizioni indicate nelle rispettive schede.",
        "Sostenere PlantAdopting aiuta lo sviluppo della piattaforma: non attiva Pro e non distribuisce automaticamente denaro alle realtà presenti."
      ],
      "startTitle": "Da dove vuoi iniziare?",
      "startFree": "Inizia gratis",
      "present": "Presenta la tua realtà",
      "managePlan": "Gestisci il piano",
      "manageSubscription": "Gestisci l’abbonamento",
      "chooseProfile": "Scegli la scheda da gestire",
      "dashboard": "Apri la dashboard",
      "formulas": "Formule di partecipazione",
      "faq": "FAQ",
      "loading": "Caricamento delle informazioni sui piani…",
      "commercialError": "Non è stato possibile verificare prezzo e limiti aggiornati. Riprova oppure consulta le condizioni complete.",
      "profilesError": "Non è stato possibile verificare le tue schede. Apri la dashboard per scegliere il piano da gestire.",
      "checkingAccount": "Verifica dell’account…",
      "retry": "Riprova",
      "tableOpportunityLimit": "{count} complessive, progetti inclusi",
      "tableProjectLimit": "Entro le {count} opportunità",
      "detailsTitle": "Dettagli su Pro e pagamenti",
      "subscriptionDetailsTitle": "Abbonamento Pro: attivazione, rinnovo e disdetta",
      "paymentsDetailsTitle": "Ricevere pagamenti e commissioni sulle offerte",
      "contributionsDetailsTitle": "Differenza tra abbonamento e contributi",
      "receivingPayments": "Per ricevere pagamenti sulle tue offerte, devi collegare e abilitare l’account Stripe del beneficiario.",
      "subscriptionDetails": [
        "L’abbonamento è per ciascuna realtà, con pagamento annuale e rinnovo automatico.",
        "Puoi disattivare il rinnovo dal portale abbonamenti. Pro resta attivo fino alla fine del periodo già pagato.",
        "La disdetta non elimina il profilo e non comporta automaticamente un rimborso."
      ]
    },
    homePolish: {
      "storyEyebrow": "Una storia dall’Atlante",
      "storyTitle": "Dall’oliveto all’Atlante",
      "storyDescription": "Una giornata di raccolta nell’Azienda Agricola Bellanca Vittoria, tra olive, persone e lavoro sul campo. Ogni realtà ha qualcosa da raccontare. Anche la tua.",
      "storyProfile": "Esplora la scheda Bellanca",
      "watchVideo": "Guarda il video",
      "videoLanguage": "Audio in italiano · sottotitoli in italiano",
      "videoUnsupported": "Il tuo browser non supporta il video.",
      "videoUnavailable": "Video non disponibile. Riprova oppure",
      "openVideoFile": "apri il file",
      "heroDescription": "Scopri aziende agricole, professionisti, luoghi e progetti che si prendono cura del territorio. Oppure presenta la tua realtà nell’Atlante.",
      "heroPlanNote": "Inizia con il piano Free. Passa a Pro se ti servono altre funzionalità.",
      "exploreWithoutAccount": "Vuoi soltanto esplorare? Puoi consultare l’Atlante senza registrarti.",
      "benefitAdoption": "Sostieni una realtà attraverso un’adozione e ricevi i benefici previsti dall’offerta, come prodotti o esperienze.",
      "freeSummary": "Free: presenta la tua realtà nell’Atlante, con contatti, informazioni sulle attività e un prodotto o produzione.",
      "proSummary": "Pro: amplia la scheda con più prodotti e opportunità — {annualPrice}."
    },
    content: {"loading": "Caricamento…", "retry": "Riprova", "catalogError": "Impossibile caricare il catalogo colture. È un errore di caricamento, non un catalogo vuoto.", "catalogEmpty": "Non ci sono ancora categorie colturali disponibili. Esplora tutti i soggetti o torna più tardi.", "cropMissing": "Categoria colturale non trovata", "cropError": "Impossibile caricare la scheda della coltura.", "cropEmpty": "Nessun soggetto dichiara ancora questa coltura. Puoi esplorare l’atlante completo o registrare la tua attività.", "homeEmpty": "L’atlante è pronto ad accogliere i primi profili. Registra un’attività o esplora i progetti quando vengono inseriti.", "homeError": "Al momento non è possibile caricare i profili. Riprova oppure apri l’atlante.", "noDescription": "Il soggetto non ha ancora inserito una descrizione.", "profileError": "Scheda del soggetto non disponibile", "profileErrorText": "La scheda potrebbe essere assente o temporaneamente non disponibile. Torna all’atlante per trovare un altro soggetto.", "profileMissing": "Scheda del soggetto non trovata", "emptyProjects": "Non sono ancora stati inseriti progetti. Comuni ed enti pubblici possono creare un progetto con Free; gli altri promotori devono avere Pro.", "noProjectMatch": "Nessun progetto corrisponde alla ricerca. Azzera i filtri per consultare tutti i progetti.", "emptySubjects": "Non ci sono ancora profili in questa categoria. Scegli un’altra categoria o registra un’attività di cui sei titolare."},
    planComparison: {
      proAnnualPrice: "50 €/anno, IVA inclusa",
      newOffersRequirePro: "Nuove offerte: richiedono Pro",
      billingNote: "Pro: {annualPrice}. Addebito unico annuale; circa 4,17 €/mese come equivalente informativo. Le nuove opportunità richiedono Pro. Sponsorizzazioni: 5%; sostegni simbolici e contributi: 0%. Costi del servizio di pagamento separati.",
    },
    nav: {
      explore: "Esplora",
      crops: "Colture",
      howItWorks: "Come funziona",
      forFarmers: "Per agricoltori e allevatori",
      about: "Chi siamo",
      signIn: "Accedi",
      listFarm: "Registra un soggetto",
      presentYourReality: "Presenta la tua realtà",
      search: "Cerca",
      menu: "Menu",
    },
    home: {
      kicker: "Coltiva relazioni. Proteggi il patrimonio vivente.",
      heroLine1: "Adotta la natura.",
      heroLine2: "Sostieni il futuro.",
      heroLine3: "Sostieni come coltivano.",
      heroSupport: "Un luogo dove aziende agricole, comunità, enti e professionisti trasformano il patrimonio vivente in progetti concreti da sostenere e seguire nel tempo.",
      searchPlaceholder: "Cerca soggetti, colture, varietà o luoghi…",
      exploreMap: "Esplora la mappa",
      listFarmFree: "Crea un profilo soggetto Free",
      statFarms: "Soggetti",
      statCrops: "Categorie colturali",
      statCountries: "Paesi",
      exploreByCrop: "Esplora per coltura",
      exploreByCropSub: "Naviga l'agricoltura in base a ciò che cresce nella terra.",
      viewAllCrops: "Vedi tutte le colture",
      moreCrops: "Altre colture",
      moreCropsSub: "Esplora tutto",
      featuredFarms: "Conosci i soggetti",
      featuredFarmsSub: "Conosci persone e organizzazioni che si prendono cura del patrimonio vivente.",
      viewAllFarms: "Apri l’atlante dei soggetti",
      forFarmersTitle: "Ti prendi cura di un luogo? Raccontalo.",
      forFarmersText: "Registra un’azienda, un allevamento, una comunità, un ente o un’attività professionale. Descrivi il patrimonio vivente che curi e proponi modi chiari per partecipare.",
      farmsCount: "soggetti",
    },
    explore: {
      title: "Esplora la mappa",
      subtitle: "Scopri soggetti, territori e opportunità. Per le attività agricole puoi filtrare anche colture e varietà.",
      searchPlaceholder: "Cerca soggetto, coltura, varietà o luogo…",
      searchLabel: "Cerca soggetti, colture, varietà o luoghi",
      filtersLoading: "Caricamento dei filtri per colture e pratiche…",
      filtersError: "Impossibile caricare i filtri per colture e pratiche. Puoi riprovare senza azzerare la ricerca.",
      referenceCatalogLoading: "Caricamento dei filtri colturali e botanici…",
      referenceCatalogError: "Filtri colturali e botanici non disponibili.",
      animalCatalogLoading: "Caricamento dei filtri per specie animali…",
      animalCatalogError: "Filtri per specie animali non disponibili.",
      allCountries: "Tutti i paesi",
      allCrops: "Tutte le colture",
      allRegions: "Tutte le regioni",
      allVarieties: "Tutte le varietà", botanicalSpecies: "Specie botanica", botanicalCultivar: "Cultivar",
      allPractices: "Tutte le pratiche",
      practicesFeatures: "Pratiche e caratteristiche",
      selectCropFirst: "Seleziona prima una coltura",
      practiceCats: {
        water_management: "Gestione dell'acqua",
        water_source: "Fonte d'acqua",
        soil: "Gestione del suolo",
        biodiversity: "Biodiversità",
        cropping_system: "Sistema colturale",
      },
      moreFilters: "Altri filtri",
      clear: "Azzera filtri",
      country: "Paese",
      region: "Regione",
      crop: "Coltura",
      variety: "Varietà",
      practice: "Pratica agricola",
      resultsFound: "soggetti trovati",
      viewFarm: "Vedi profilo",
      noResults: "Nessun soggetto corrisponde ai filtri. Prova ad ampliare la ricerca.",
      mainCrop: "Coltura principale",
    },
    farm: {
      botanicalDetails: "Specie botaniche e cultivar",
      overview: "Panoramica",
      fields: "Campi",
      cropsVarieties: "Colture e varietà",
      howWeGrow: "Come coltiviamo",
      products: "Prodotti",
      experiences: "Esperienze",
      supportAdopt: "Sostieni e adotta",
      aboutFarm: "Informazioni sul soggetto",
      location: "Posizione",
      mainCrops: "Colture principali",
      contact: "Contatti",
      website: "Sito web",
      profileCompleteness: "Completezza del profilo",
      ourFields: "Aree e appezzamenti",
      area: "Superficie",
      crop: "Coltura",
      trees: "Alberi",
      orchardAge: "Età dell’impianto",
      plantingSpacing: "Sesto d'impianto",
      scientificName: "Nome scientifico",
      varieties: "Varietà",
      toBeVerified: "Da verificare",
      waterManagement: "Gestione dell'acqua",
      soilManagement: "Gestione del suolo",
      biodiversity: "Biodiversità",
      agriculturalPractices: "Pratiche agricole",
      waterSource: "Fonte d'acqua",
      fromThisFarm: "Prodotti e servizi",
      visitShop: "Visita lo shop",
      connect: "Connettiti",
      years: "anni",
      yearsOld: "anni",
      viewAllProducts: "Vedi tutti i prodotti",
      noValuesYet: "Informazioni non ancora fornite.",
      demoFarm: "Profilo DEMO",
      farmLabel: "Soggetto",
      farmerDeclared: "Dichiarato dal soggetto",
      declaredNote: "Le informazioni sono dichiarate dal soggetto e non verificate in modo indipendente salvo indicazione specifica. La completezza non è una certificazione.",
      lastUpdated: "Ultimo aggiornamento",
      contactFarm: "Contatta il soggetto",
      learnMore: "Scopri di più",
    },
    onboarding: {
      title: "Registra un soggetto",
      subtitle: "Crea un profilo base in pochi minuti. È gratis.",
      step: "Passo",
      of: "di",
      step1: "Crea la scheda del soggetto",
      step2: "Cosa coltivi?",
      step3: "Racconta la tua storia",
      step4: "Aggiungi i contatti",
      farmName: "Nome pubblico del soggetto",
      country: "Paese",
      region: "Regione",
      locality: "Località",
      selectCrops: "Seleziona una o più colture", botanicalSpecies: "Specie botanica (facoltativa)", selectSpecies: "Seleziona una specie", botanicalCultivar: "Cultivar (facoltativa)", selectCultivar: "Seleziona una cultivar", noCultivar: "Non specificata", otherCultivar: "Altra cultivar", otherCultivarPlaceholder: "Inserisci il nome della cultivar", otherCultivarNote: "I nomi inseriti dagli utenti non sono automaticamente verificati né aggiunti al catalogo.",
      description: "Breve descrizione",
      mainImage: "Immagine di copertina",
      websiteOpt: "Sito web (facoltativo)",
      shopOpt: "URL shop (facoltativo)",
      next: "Avanti",
      back: "Indietro",
      finish: "Crea la scheda del soggetto",
      doneTitle: "La scheda del soggetto è stata creata.",
      doneSub: "Completa informazioni e condizioni di partecipazione dalla dashboard. La completezza del profilo non è una verifica.",
      profileComplete: "Il tuo profilo è completo al",
      complete: "",
      enrich: "Arricchisci il profilo",
      viewMyFarm: "Vedi la scheda del soggetto", goDashboard: "Vai alla mia dashboard",
      required: "Campo obbligatorio",
      addVarieties: "Aggiungi varietà",
      addField: "Aggiungi la prima area / appezzamento",
      tellHow: "Raccontaci come coltivi",
      addCerts: "Aggiungi certificazioni",
      addProducts: "Aggiungi un prodotto / produzione",
      addOpps: "Attiva Pro per aggiungere opportunità",
    },
    completeness: {
      subject_identity: "Identità del soggetto", description: "Descrizione", contact: "Contatti",
      farm_identity: "Identità del soggetto",
      location: "Posizione",
      main_crop: "Colture indicate",
      field_information: "Informazioni sui campi",
      varieties: "Aggiungi varietà",
      farming_practices: "Aggiungi pratiche agricole",
      certifications: "Aggiungi certificazioni",
      opportunities: "Aggiungi opportunità",
    },
    opp: {
      BUY: "Acquista", ADOPT: "Adotta", VISIT: "Visita", LEARN: "Impara",
      VOLUNTEER: "Volontariato", SUPPORT: "Sostieni", PROJECT: "Progetto",
    },
    footer: {
      tagline: "Adotta la natura. Sostieni il futuro.",
      explore: "Esplora",
      company: "PlantAdopting",
      about: "Chi siamo",
      forFarmers: "Per agricoltori e allevatori",
      howItWorks: "Come funziona",
      crops: "Colture",
      support: "Sostieni PlantAdopting",
      note: "PlantAdopting connette patrimonio vivente, soggetti e territori. I dati dei profili sono dichiarati dai soggetti. I pagamenti abilitati sono gestiti tramite Stripe. Gli addebiti live sono reali.",
      rights: "Tutti i diritti riservati.",
    },
    support: {
      title: "Sostieni PlantAdopting",
      intro: "PlantAdopting è un atlante del patrimonio vivente che collega persone, aziende, enti, professionisti e territori. Il sostegno contribuisce allo sviluppo della piattaforma.",
      howHeading: "Come il sostegno può aiutare",
      howSub: "Con la crescita del progetto, il sostegno potrebbe aiutare PlantAdopting a:",
      disclaimer: "PlantAdopting è un progetto in sviluppo e non è un'organizzazione non profit, un ente di beneficenza, una fondazione o un'associazione. Il sostegno volontario alla piattaforma è separato dai pagamenti ai soggetti e dagli abbonamenti. Verifica disponibilità, importo e condizioni nel checkout; i pagamenti live abilitati sono reali. Non è promessa deducibilità fiscale.",
    },
    auth: {
      login: "Accedi", register: "Crea account", logout: "Esci",
      email: "Email", password: "Password", name: "Nome",
      signInTitle: "Bentornato", signInSub: "Accedi per partecipare, consultare i pagamenti e gestire i tuoi soggetti.",
      registerTitle: "Crea il tuo account", registerSub: "Un account personale gratuito per partecipare e gestire le tue attività.",
      noAccount: "Non hai un account?", haveAccount: "Hai già un account?",
      myDashboard: "La mia dashboard", myFarm: "Il mio soggetto", editFarm: "Modifica la scheda del soggetto",
      dashboardTitle: "La mia dashboard", yourFarms: "I tuoi soggetti", allFarms: "Tutti i soggetti", farmFilter_all: "Tutte", farmFilter_assigned: "Assegnate", farmFilter_unassigned: "Non assegnate", noMatchingFarms: "Nessun soggetto corrisponde a questo filtro.", farmsLoadError: "Impossibile caricare i soggetti. Riprova più tardi.", ownerAssignedStatus: "Proprietario assegnato", ownerUnassignedStatus: "Nessun proprietario assegnato", ownerAssign: "Assegna proprietario", ownerChange: "Cambia proprietario", ownerEmailLabel: "Email del proprietario registrato", ownerSave: "Salva proprietario", ownerCancel: "Annulla", ownerInvalidEmail: "Inserisci un indirizzo email valido.", ownerNotFound: "Nessun account registrato trovato per questa email.", ownerAssignError: "Impossibile assegnare il proprietario. Riprova.", ownerAssigned: "Titolare del soggetto aggiornato.",
      noFarms: "Nessun soggetto è ancora collegato al tuo account. Puoi partecipare come adottante o registrare un’attività di cui sei titolare. Le schede esistenti richiedono un’assegnazione verificata.",
      openPublic: "Vedi profilo pubblico", edit: "Modifica", plan: "Piano",
      save: "Salva modifiche", saving: "Salvataggio…", saved: "Modifiche salvate.",
      saveError: "Impossibile salvare. Riprova.", loading: "Caricamento…",
      required: "Obbligatorio", passwordHint: "Almeno 8 caratteri",
      adminBadge: "Admin", ownerBadge: "Proprietario",
      secGeneral: "Informazioni generali", secFields: "Aree / appezzamenti", secCrops: "Colture e varietà",
      secPractices: "Pratiche agricole", secBrands: "Marchi e prodotti", secOpps: "Opportunità",
      farmName: "Nome pubblico del soggetto", description: "Descrizione (inglese)", descriptionIt: "Descrizione (italiano)",
      locality: "Località", municipality: "Comune", region: "Regione", country: "Paese",
      mainImage: "Immagine di copertina", website: "Sito web", contactEmail: "Email di contatto", shopUrl: "URL shop",
      addField: "Aggiungi area / appezzamento", addCrop: "Aggiungi coltura", addProduct: "Aggiungi prodotto", addOpportunity: "Aggiungi opportunità",
      remove: "Rimuovi", fieldNameLabel: "Nome campo", areaHa: "Superficie (ha)", crop: "Coltura", trees: "Alberi",
      waterMethods: "Metodi d'acqua (separati da virgola)", waterSource: "Fonte d'acqua",
      scientificName: "Nome scientifico", varieties: "Varietà (separate da virgola)",
      brandName: "Nome marchio", productName: "Nome prodotto", externalUrl: "Link esterno",
      soil: "Gestione del suolo (separati da virgola)", biodiversity: "Biodiversità (separati da virgola)",
      oppType: "Tipo", oppTitle: "Titolo", backToDash: "Torna alla dashboard",
    },
    pages: {
      howTitle: "Come funziona PlantAdopting",
      howSub: "Un atlante del patrimonio vivente che connette persone, soggetti, territori e progetti.",
      forFarmersTitle: "Metti la tua attività sulla mappa",
      forFarmersSub: "Unisciti a un atlante in crescita di aziende reali. Condividi la tua storia e connettiti con chi ha a cuore ciò che coltivi.",
      aboutTitle: "Chi è PlantAdopting",
      aboutSub: "Un atlante del patrimonio vivente, delle persone e dei territori.",
      cropsTitle: "Colture, specie e varietà",
      cropsSub: "Esplora le categorie agricole e i profili che dichiarano queste colture.",
    },
  },
};

// Controlled vocabulary: canonical English term -> Italian display label.
// Proper names, brand names, cultivar/variety names and scientific names are NOT included
// here and therefore pass through untranslated.
export const VOCAB = {
  // crops & crop types
  "Olive": "Olivo", "Grapes": "Vite", "Citrus": "Agrumi", "Nuts": "Frutta a guscio",
  "Vegetables": "Ortaggi", "Cereals": "Cereali", "Orchards": "Frutteti",
  "Aromatic plants": "Piante aromatiche", "Lavender": "Lavanda", "Wheat": "Frumento",
  "Olive grove": "Oliveto", "Lavender fields": "Campi di lavanda", "Vineyard": "Vigneto",
  // water methods
  "Rain-fed": "Coltura in asciutto", "Irrigated": "Irrigato", "Drip irrigation": "Irrigazione a goccia",
  "Micro-sprinkler": "Microaspersione", "Sprinkler": "Aspersione", "Surface irrigation": "Irrigazione superficiale",
  // water sources
  "Well": "Pozzo", "Surface water": "Acque superficiali", "Reservoir": "Invaso",
  "Rainwater collection": "Raccolta di acqua piovana", "Public irrigation network": "Rete irrigua pubblica",
  "Other": "Altro",
  // soil management
  "Cover crops": "Colture di copertura", "Mulching": "Pacciamatura", "Mechanical mowing": "Sfalcio meccanico",
  "Tillage": "Lavorazione del terreno", "Reduced tillage": "Lavorazione ridotta", "No-till": "Semina su sodo",
  "Composting": "Compostaggio", "Organic amendments": "Ammendanti organici",
  // biodiversity
  "Hedgerows": "Siepi", "Native vegetation": "Vegetazione autoctona", "Beehives": "Alveari",
  "Flower strips": "Fasce fiorite", "Bird habitat": "Habitat per uccelli", "Agroforestry": "Agroforestazione",
  // seasons
  "Spring": "Primavera", "Summer": "Estate", "Autumn": "Autunno", "Winter": "Inverno",
  // countries
  "Italy": "Italia", "France": "Francia", "Spain": "Spagna", "Portugal": "Portogallo", "Greece": "Grecia",
  "Germany": "Germania", "United States": "Stati Uniti", "United Kingdom": "Regno Unito", "Morocco": "Marocco",
  "Tunisia": "Tunisia", "Croatia": "Croazia", "Turkey": "Turchia",
  // regions
  "Sicily": "Sicilia", "Tuscany": "Toscana", "Provence": "Provenza", "La Rioja": "La Rioja",
  "Alentejo": "Alentejo", "Puglia": "Puglia",
};

// Translate a controlled-vocabulary term; pass through unknown values (proper names, cultivars).
export const localizeTerm = (value, lang, context) => {
  if (value == null) return value;
  if (lang === "it" && value === "Spring" && context === "water_source") return "Sorgente";
  if (lang === "it") return VOCAB[value] || value;
  return value;
};

// Resolve a value that may be a localized dict {en, it} or a plain string.
export const localize = (value, lang) => {
  if (value && typeof value === "object") return value[lang] || value.en || value.it || "";
  return value;
};



